import React, {useState, useEffect} from "react";
import {Skeleton, Grid, Box, Typography} from "@mui/material";
import {LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer} from "recharts";
import DateRangePickerComponent from "../../common/CustomDateRangePicker";
import moment from "moment";
import useDashboard from "../../../hooks/useDashboard";
import EmptyIcon from "../../../assets/images/empty-box.png";
import useDashboardResetFilterStore from "../../../zustand/useDashboardResetFilterStore";


const AuthenticationStatusComponent = (props) => {
    const {breakpoints} = props;

    const {isDashboardResetFilters, setDashboardResetFilters} = useDashboardResetFilterStore();
    const {getDashboardAuthenticationStatus, authenticationStatus, isFetchingDashboardAuthenticationStatus} = useDashboard();

    const [startDate, setStartDate] = useState(moment().subtract(6, 'days').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
    const [authenticationStatusData, setAuthenticationStatusData] = useState([]);

    
    useEffect(() => {
        if (isDashboardResetFilters) {
            setStartDate(moment().subtract(6, "days").format("YYYY-MM-DD"));
            setEndDate(moment().format("YYYY-MM-DD"));
        }
    }, [isDashboardResetFilters]);

    useEffect(() => {
        if (startDate && endDate) { 
            getDashboardAuthenticationStatus(startDate, endDate);
            setDashboardResetFilters(false);
        }
    }, [startDate, endDate])

    useEffect(() => {
        if (authenticationStatus) {
            const transformData = (authenticationStatus) => {
                const result = {};
            
                const processEntry = (entry) => {
                    const month = moment(entry.date).format("MMMM YYYY"); 
            
                    if (!result[month]) {
                        result[month] = {
                            date: month,
                            successful: 0, 
                            pending: 0,
                            failed: 0      
                        };
                    }
            
                    entry.statuses.forEach(status => {
                        let statusKey;

                        if (status.status === "Completed") {
                            statusKey = "successful"; 

                        } else if (status.status === "Error") {
                            statusKey = "failed"; 

                        } else {
                            statusKey = status.status.toLowerCase();
                        }

                        result[month][statusKey] += status.count;
                    });
                }
            
                authenticationStatus?.product?.forEach(processEntry);
                authenticationStatus?.document?.forEach(processEntry);
            
                return Object.values(result);
            }

            setAuthenticationStatusData(transformData(authenticationStatus));
        }
    }, [authenticationStatus])


    const handleDateRangeChange = (startDate, endDate) => {
        setStartDate(startDate);
        setEndDate(endDate)
    }


    return (
        <Box sx={{backgroundColor: "#fff", mt: "20px", padding: "24px", pt: "24px", borderRadius: "8px"}}>
            <Grid container>
                <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <Typography sx={{color: "#58565f", fontSize: 18, mb: 3, fontWeight: 600}}>
                        Authentication Status
                    </Typography>
                </Grid>

                <Grid item xs={6} sm={6} md={6} lg={6} xl={6} sx={{textAlign: "end", justifyContent: "end", ml: "auto", display: "flex"}}>   
                    <DateRangePickerComponent 
                        initialStartDate={startDate}
                        initialEndDate={endDate}
                        ranges={{
                            "Last 7 Days": [moment().subtract(6, 'days'), moment()],
                            "This Month": [moment().startOf('month'), moment().endOf('month')],
                            "Last Month": [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
                            "Last 90 Days": [moment().subtract(89, 'days'), moment()],
                        }}
                        onDateRangeChange={handleDateRangeChange}
                    />
                </Grid>
            </Grid>

            {isFetchingDashboardAuthenticationStatus ?
                <Skeleton variant="rounded" sx={{backgroundColor: "#d8d8fdbd"}} height={617} />
            :
                (!isFetchingDashboardAuthenticationStatus && authenticationStatusData?.length === 0) ?
                    <Box sx={{height: 617, textAlign: "center", flexDirection: "column", 
                        justifyContent: "center", display: "flex", alignItems: "center", 
                    }}>
                        <img alt="empty-data-icon" src={EmptyIcon} width="200" style={{marginTop: "-84px"}} />

                        <Typography sx={{color: "#58565f", fontSize: 14, mt: 1}}>
                            No data available for the selected date range
                        </Typography>
                    </Box>
            :
                <ResponsiveContainer width="100%" height={617} style={{paddingTop: "12px"}}>
                    <LineChart width={500} height={510} data={authenticationStatusData}
                        margin={{top: 5, right: 30, left: 20, bottom: 5}}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />

                        <YAxis />
                        <Tooltip /> 
                        <Legend />

                        <Line type="monotone" dataKey="successful" name="Successful" stroke="#40BF7E" activeDot={{r: 8}} strokeWidth={1.5} />
                        <Line type="monotone" dataKey="pending" name="Pending" stroke="#FFA500" strokeWidth={1.5} />
                        <Line type="monotone" dataKey="failed" name="Failed" stroke="#E76C59" strokeWidth={1.5} />
                    </LineChart>
                </ResponsiveContainer>
            }
        </Box>
    )

}

export default AuthenticationStatusComponent